import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { floor } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { Rooms } from '../size-module/mongolia-standard-table/mongolia-standard-table.types';
import * as _ from 'lodash';

type ColumnData = Record<string, number>

@Component({
  selector: 'kt-accommodation-layout-table',
  templateUrl: './accommodation-layout-table.component.html',
  styleUrls: ['./accommodation-layout-table.component.scss']
})
export class AccommodationLayoutTableComponent implements OnInit {
  @Input() propertySubTypeID: number;
  private _specialPurposeTypeID: number = undefined
  @Input()
  set specialPurposeTypeID(value: number) {
    this._specialPurposeTypeID = value
    this.tableRows = this._getRows(this.propertySubTypeID, this._specialPurposeTypeID)
  }
  @Input() aboveFloor: Observable<number>;
  @Input() belowFloor: Observable<number>;
  @Input() initData: Record<number, ColumnData>;
  @Input() initOthers: { id: number, title: string }[];
  @Input() schemeId: number = 1;
  @Input() hasTitle: boolean = true;
  @Input() readonly: boolean = false;
  @Input() floors$: Observable<number[]> = of([]);
  @Input() rooms$: Observable<{ room: number, bedroom: number, bathroom: number }> = of(null);
  @Output() roomChange: EventEmitter<Rooms> = new EventEmitter();

  data: Record<number, ColumnData> = {};
  floors: number[] = [];
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  others: { id: number, title: string }[] = [];
  tableRows: AccomodationRow = { kind: 'default', rows: [] };

  constructor() { }

  ngOnInit(): void {
    this.tableRows = this._getRows(this.propertySubTypeID, this._specialPurposeTypeID);
    this.data = this._deepCopy(this.initData);
    this.others = this.initOthers.map(io => Object.assign({}, io));
    if (this.others.length == 0) {
      this.others.push({ id: 1, title: null });
    }
    const rooms: Rooms = {
      bath: 0,
      kitchen: 0,
      bedrooms: 0,
      livingRooms: 0,
      toilet: 0
    }
    _.each(this.data, (col) => {
      Object.entries(col).forEach(([key, value]) => {
        if (rooms.hasOwnProperty(key)) {
          rooms[key] += value != undefined ? Number(value) : 0;
        }
      })
    })
    this.roomChange.emit(rooms);

    combineLatest([
      // this.aboveFloor,
      // this.belowFloor,
      this.floors$,
      this.rooms$,
    ]).subscribe(([floors, rooms]) => {
      this.loading$.next(true);
      let _floors = [];
      // if (above || below) {
      //   _floors = this._computeFloorNumbers(above, below);
      // } else {
      //   _floors = floors;
      // }
      _floors = floors;
      if (rooms == null) {
        // This branch will work on any other comparable other than Apartment
        _floors.forEach(floor => {
          if (this.data[floor]) {
            return;
          }
          this.data[floor] = {};
          this.others.forEach(other => this.data[floor][other.id] = undefined);
        });
      } else {
        _floors.forEach(floor => {
          if (this.data[floor]) {
            return;
          }
          this.data[floor] = {};
          this.others.forEach(other => this.data[floor][other.id] = undefined);
        });
        if (_floors.length > 0) {
          this.data[_floors[0]]['total'] = rooms.room;
          this.data[_floors[0]]['bedrooms'] = rooms.bedroom;
          this.data[_floors[0]]['bath'] = rooms.bathroom;
        }
      }
      this.floors = _floors;
    })
  }

  removeOther(index: number, id: number) {
    this.others.splice(index, 1);
    Object.values(this.data).forEach(val => {
      delete val[id];
    });
  }
  addOther(index: number) {
    const newId = this.others.length + 1;
    this.others.push({ id: newId, title: '' });
    Object.values(this.data).forEach(val => {
      val[newId] = undefined;
    });
  }

  public getData() {
    return {
      data: this.data,
      others: this.others,
    }
  }

  private _computeFloorNumbers(above: number, below: number): number[] {
    const floors: number[] = [];
    for (let i = -below; i <= above; i++) {
      if (i == 0) {
        continue;
      }
      floors.push(i);
    }
    floors.sort((a, b) => a - b);
    return floors;
  }

  private _deepCopy(src: Record<number, Record<string, number>>) {
    if (src == null) {
      return {};
    }
    const dst = {};
    Object.keys(src).forEach(key => {
      const item = {};
      Object.keys(src[key]).forEach(_key => {
        item[_key] = src[key][_key];
      });
      dst[key] = item;
    });
    return dst;
  }

  private _getRows(id: number, specialPurposeTypeID?: number): AccomodationRow {
    switch (id) {
      case 1: {
        return {
          kind: 'default',
          rows: [
            { title: "Total number of rooms", key: "total" },
            { title: 'Living rooms', key: 'livingRooms' },
            { title: 'Bedrooms', key: 'bedrooms' },
            { title: 'Bath or Shower', key: 'bath' },
            { title: 'Separate toilet', key: 'toilet' },
            { title: 'Kitchen', key: 'kitchen' },
            { title: 'Utility room', key: 'utility_room' },
            { title: 'Conservatory', key: 'conservatory' }
          ]
        }
      }
      case 3: {
        return {
          kind: 'default',
          rows: [
            { title: 'Reception area', key: 'reception_area' },
            { title: 'Private office', key: 'private_office' },
            { title: 'Open area', key: 'open_area' },
            { title: 'Conference room', key: 'conference_room' },
            { title: 'Break room / printer room', key: 'break_room' },
            { title: 'Mail room', key: 'mail_room' },
            { title: 'Hygiene area', key: 'hygiene_area' },
            { title: 'Kitchen', key: 'kitchen' },
          ]
        };
      }
      case 5: {
        return {
          kind: 'default',
          rows: [
            { title: 'Retailing area', key: 'retailing_area' },
            { title: 'Storage', key: 'storage' },
            { title: 'Office', key: 'office' },
            { title: 'Break room', key: 'break_room' },
            { title: 'Sales counter', key: 'sales_counter' },
            { title: 'Utility room', key: 'utility_room' }
          ]
        }
      }
      case 7: {
        return {
          kind: 'default',
          rows: [
            { title: 'Warehouse', key: 'warehouse' },
            { title: 'Production', key: 'production' },
            { title: 'Office', key: 'office' },
            { title: 'Showroom', key: 'showroom' },
            { title: 'Mezzanine', key: 'mezzanine' },
            { title: 'Loading docks', key: 'loading_docks' },
            { title: 'Utility room', key: 'utility_room' }
          ]
        };
      }
      case 11: {
        return {
          kind: 'default',
          rows: [
            { title: 'Retailing area', key: 'retailing_area' },
            { title: 'Storage', key: 'storage' },
            { title: 'Office', key: 'office' },
            { title: 'Break room', key: 'break_room' },
            { title: 'Sales counter', key: 'sales_counter' },
            { title: 'Utility room', key: 'utility_room' }
          ]
        }
      }
      case 17: {
        return {
          kind: 'default',
          rows: [
            { title: 'Living rooms', key: 'livingRooms' },
            { title: 'Bedrooms', key: 'bedrooms' },
            { title: 'Bath or Shower', key: 'bath' },
            { title: 'Separate toilet', key: 'toilet' },
            { title: 'Kitchen', key: 'kitchen' },
            { title: 'Utility room', key: 'utility_room' },
            { title: 'Conservatory', key: 'conservatory' }
          ]
        }
      }
      case 18: {
        return {
          kind: 'special',
          rows: specialPurposeRows(specialPurposeTypeID)
        }
      }
    }
  }

  onChange(event: any, key: string) {
    const rooms: Rooms = {
      bath: 0,
      kitchen: 0,
      bedrooms: 0,
      livingRooms: 0,
      toilet: 0
    }
    _.each(this.data, (col) => {
      Object.entries(col).forEach(([key, value]) => {
        if (rooms.hasOwnProperty(key)) {
          rooms[key] += value != undefined ? Number(value) : 0;
        }
      })
    })
    this.roomChange.emit(rooms);
  }
}

function specialPurposeRows(specialPurposeTypeID: number): SpecialAccomodationRow['rows'] {
  if (specialPurposeTypeID == 1) {
    return [
      {
        category: 'Main Congregation / Worship Area',
        key: 'main_congregation',
        tooltip: 'This is the primary space for services and includes seating (pews, chairs, or open floor space), the main altar or focal point (pulpit, bimah, mihrab), and any associated religious iconography.',
        subrows: []
      },
      {
        category: 'Ancillary & Support Area', subrows: [
          { title: 'Entrance / Narthex / Foyer', key: 'entrance', tooltip: 'A transition space at the main entrance.' },
          { title: 'Administrative offices', key: 'administrative_offices', tooltip: 'For clergy and staff.' },
          { title: 'Classrooms / Meeting rooms', key: 'classrooms', tooltip: 'For religious education, community gatherings, and events.' },
          { title: 'Kitchen / Fellowship hall', key: 'kitchen', tooltip: 'A space for social events and meals.' },
          { title: 'Restrooms', key: 'restrooms', tooltip: 'Public and private facilities.' },
          { title: 'Chapel / Prayer rooms', key: 'chapel', tooltip: 'Smaller, more intimate spaces for prayer or smaller services.' },
          { title: 'Storage Rooms', key: 'storage_rooms', tooltip: 'For supplies, decorations, and equipment.' },
          { title: 'Bell Tower / Minaret', key: 'bell_tower', tooltip: 'Structural elements specific to certain faiths.' },
          { title: 'Parking', key: 'parking', tooltip: ' On-site parking facilities.' }
        ]
      }]
  }
  if (specialPurposeTypeID == 2) {
    return [
      {
        category: 'Instructional area', subrows: [
          { title: 'Classrooms', key: 'classrooms', tooltip: 'Standard teaching spaces.' },
          { title: 'Lecture Halls', key: 'lecture_halls', tooltip: 'Larger tiered seating for lectures.' },
          { title: 'Laboratories', key: 'laboratories', tooltip: 'Specialised for science, computers, languages, etc.' },
          { title: 'Workshops', key: 'workshops', tooltip: 'For vocational or technical training.' },
          { title: 'Art / Music Studios', key: 'art_studios', tooltip: 'Designed for creative arts.' }]
      },
      {
        category: 'Support and Administrative Areas', subrows: [
          { title: 'Library / Media Center', key: 'library', tooltip: 'For research and study.' },
          { title: 'Auditorium / Assembly Hall', key: 'auditorium', tooltip: 'For large gatherings and performances.' },
          { title: 'Gymnasium and Athletic Facilities', key: 'gymnasium', tooltip: 'Indoor and outdoor sports areas, including changing rooms.' },
          { title: 'Cafeteria / Dining Hall', key: 'cafeteria', tooltip: 'With kitchen and serving areas.' },
          { title: 'Administrative Offices', key: 'administrative_offices', tooltip: 'For the principal, department heads, and support staff.' },
          { title: 'Staff Lounge / Workrooms', key: 'staff_lounge', tooltip: 'For teachers and faculty.' },
          { title: 'Student Services', key: 'student_services', tooltip: 'Counselling offices, health clinic, etc.' },
          { title: 'Restrooms', key: 'restrooms' },
          { title: 'Storage', key: 'storage', tooltip: 'For educational materials and equipment.' },
          { title: 'Parking', key: 'parking', tooltip: 'For staff, students, and visitors.' }
        ]
      }]
  }
  if (specialPurposeTypeID == 3) {
    return [
      {
        category: 'Public & Semi-Public Areas', subrows: [
          { title: 'Main Lobby & Security Checkpoint', key: 'main_lobby_security_checkpoint', tooltip: 'The single point of controlled public entry' },
          { title: "Clerk of Court's Office", key: 'clerk_of_court_s_office', tooltip: 'Public counters for filing documents, paying fines, and accessing public records' },
          { title: 'Courtrooms', key: 'courtrooms' },
          { title: 'Jury Assembly Room', key: 'jury_assembly_room', tooltip: 'A large waiting area for prospective jurors' },
          { title: 'Attorney-Client Conference Rooms', key: 'attorney_client_conference_rooms', tooltip: 'Small, private rooms near courtrooms' },
          { title: 'Victim / Witness Waiting Rooms', key: 'victim_witness_waiting_rooms' },
          { title: 'Press Room', key: 'press_room' },
        ]
      },
      {
        category: 'Restricted (Judical & Staff) Areas', subrows: [
          { title: "Judges' Chambers", key: 'judges_chambers', tooltip: 'Private offices for judges, typically with a private restroom and space for a law clerk and secretary, located in a secure corridor' },
          { title: 'Jury Deliberation Rooms', key: 'jury_deliberation_rooms', tooltip: 'Secure rooms attached directly to courtrooms' },
          { title: 'Law Library', key: 'law_library', tooltip: 'For use by judges and legal staff' },
          { title: "Prosecutor's / District Attorney's Offices", key: 'prosecutor_s_district_attorney_s_offices' },
          { title: "Public Defender's Offices", key: 'public_defender_s_offices' },
          { title: 'Secure Corridors', key: 'secure_corridors', tooltip: 'Separate, non-public hallways for judges and staff to move between chambers and courtrooms.' },
        ]
      },
      {
        category: 'Secure (In-Custody) Areas', subrows: [
          { title: 'Central Holding Cells', key: 'central_holding_cells', tooltip: 'A secure area, usually in the basement or on the ground floor, to hold detainees awaiting court appearances' },
          { title: 'Secure Sally Port', key: 'secure_sally_port', tooltip: 'A controlled vehicle bay for the secure transfer of prisoners from transport vehicles into the courthouse' },
          { title: 'Secure Elevators & Corridors', key: 'secure_elevators_corridors', tooltip: 'A third, completely separate circulation path to move detainees between the holding area and courtrooms without any contact with the public or judiciary' },
        ]
      }];
  }
  if (specialPurposeTypeID == 4) {
    return [
      {
        category: 'Public Service & Legislative Areas', subrows: [
          { title: "Council Chambers", key: 'council_chambers', tooltip: "A large, formal room for city council meetings, similar to a legislative chamber with dais seating for council members, a clerk's station, a podium for public speakers, and public gallery seating" },
          { title: "Departmental Service Counters", key: 'departmental_service_counters', tooltip: "- Permitting & Planning Department: For building permits and zoning inquiries.\n - Utilities/Finance Department: For paying taxes and water bills.\n - City Clerk's Office: For voter registration, business licenses, and public records requests." },
          { title: "Public Meeting Rooms", key: 'public_meeting_rooms', tooltip: 'A large waiting area for prospective jurors' },
        ]
      },
      {
        category: 'Official & Administrative Areas', subrows: [
          { title: "Mayor's Office Suite", key: 'mayor_s_office_suite', tooltip: 'A set of offices for the mayor, chief of staff, and administrative support' },
          { title: "City Manager/Administrator's Office", key: 'city_manager_administrator_s_office' },
          { title: 'Offices for Various City Departments', key: 'offices_for_various_city_departments', tooltip: 'For Public Works, Parks and Recreation, Finance, Human Resource, IT' },
          { title: 'Conference Rooms & Caucus Rooms', key: 'conference_rooms_and_caucus_rooms' },
          { title: 'Press Briefing Room', key: 'press_briefing_room' },
          { title: 'Historical Archives / Records Vault', key: 'historical_archives_records_vault', tooltip: 'Separate, non-public hallways for judges and staff to move between chambers and courtrooms' }
        ]
      }
    ]
  }
  if (specialPurposeTypeID == 5) {
    return [
      {
        category: 'Patient Care Area', subrows: [
          { title: "Patient Rooms", key: 'patient_rooms', tooltip: 'Inpatient rooms (private, semi-private).' },
          { title: "Emergency Department (ED/ER)", key: 'emergency_department_ed_er', tooltip: 'With triage, treatment bays, and trauma rooms.' },
          { title: "Intensive Care Units (ICU)", key: 'intensive_care_units_icu', tooltip: 'Specialized for critical care.' },
          { title: "Surgical Suites", key: 'surgical_suites', tooltip: 'Including operating rooms, pre-op, and post-op recovery areas' },
          { title: "Maternity/Labor and Delivery Suites", key: 'maternity_labor_and_delivery_suites' },
          { title: "Outpatient Clinics", key: 'outpatient_clinics', tooltip: 'For various specialties' },
          { title: "Diagnostic Imaging", key: 'diagnostic_imaging', tooltip: 'Rooms for X-ray, CT, MRI, ultrasound.' },
        ]
      },
      {
        category: "Ancillary and Support Services", subrows: [
          { title: "Laboratory", key: 'laboratory', tooltip: 'For sample analysis.' },
          { title: "Pharmacy", key: 'pharmacy', tooltip: 'Inpatient and sometimes outpatient.' },
          { title: "Central Sterile Supply Department", key: 'central_sterile_supply_department' },
          { title: "Morgue", key: 'morgue' },
          { title: "Rehabilitation Services", key: 'rehabilitation_services', tooltip: 'Physical therapy, occupational therapy gyms.' },
        ]
      },
      {
        category: "Administrative and Public Areas", subrows: [
          { title: "Main Lobby/Reception/Admissions", key: 'main_lobby_reception_admissions' },
          { title: "Administrative Offices", key: 'administrative_offices' },
          { title: "Medical Records Department", key: 'medical_records_department' },
          { title: "Cafeteria and Kitchen", key: 'cafeteria_and_kitchen' },
          { title: "Chapel/Meditation Room", key: 'chapel_meditation_room' },
          { title: "Gift Shop", key: 'gift_shop' },
          { title: "Parking Garage/Lots", key: 'parking_garage_lots' },
        ]
      }]
  }
  if (specialPurposeTypeID == 6) {
    return [
      {
        category: "Performance/Audience Area", subrows: [
          { title: "Auditorium/Seating Area", key: 'auditorium_seating_area', tooltip: 'With fixed seating (tiered or flat).' },
          { title: "Stage/Screen Area/Playing Field", key: 'stage_screen_area_playing_field' },
          { title: "Box Seats/VIP Suites", key: 'box_seats_vip_suites' },
          { title: "Front of House", key: 'front_of_house' },
        ]
      },
      {
        category: "Lobby/Foyer", subrows: [
          { title: "Box Office/Ticketing Area", key: 'box_office_ticketing_area' },
          { title: "Concession Stands", key: 'concession_stands' },
          { title: "Restrooms", key: 'restrooms' },
          { title: "Merchandise Sales Areas", key: 'merchandise_sales_areas' },
        ]
      },
      {
        category: "Back of House/Support Areas", subrows: [
          { title: "Dressing Rooms", key: 'dressing_rooms' },
          { title: "Green Room", key: 'green_room' },
          { title: "Rehearsal Spaces", key: 'rehearsal_spaces' },
          { title: "Production/Control Booths", key: 'production_control_booths' },
          { title: "Stage Wings and Fly Loft", key: 'stage_wings_and_fly_loft' },
          { title: "Loading Docks", key: 'loading_docks' },
          { title: "Administrative Offices", key: 'administrative_offices' },
          { title: "Locker Rooms (for arenas)", key: 'locker_rooms_for_arenas' },
        ]
      }]
  }
  if (specialPurposeTypeID == 7) {
    return [
      {
        category: 'Gas station elements', subrows: [
          { title: "Fuelling Islands with Dispensers (Pumps)", key: 'fuelling_islands_with_dispensers_pumps' },
          { title: "Canopy over Fueling Islands", key: 'canopy_over_fueling_islands' },
          { title: "Underground Storage Tanks (USTs)", key: 'underground_storage_tanks_usts' },
          { title: "Convenience Store/Retail Building", key: 'convenience_store_retail_building' },
          { title: "Payment Kiosk/Booth", key: 'payment_kiosk_booth' },
          { title: "Air/Water Station", key: 'air_water_station' },
          { title: "Parking and Circulation Areas", key: 'parking_and_circulation_areas' },
          { title: "Site Signage/Pylon Sign", key: 'site_signage_pylon_sign' },
        ]
      },
      {
        category: 'Car Wash elements', subrows: [
          { title: "Wash Tunnel/Bay (automated or self-service)", key: 'wash_tunnel_bay_automated_or_self_service' },
          { title: "Drying Area", key: 'drying_area' },
          { title: "Vacuuming Stations", key: 'vacuuming_stations' },
          { title: "Water Reclamation System", key: 'water_reclamation_system' },
          { title: "Equipment/Control Room", key: 'equipment_control_room' },
        ]
      }]
  }
  if (specialPurposeTypeID == 8) {
    return [
      {
        category: 'Temperature-Controlled Storage Areas', subrows: [
          { title: "Freezer Rooms", key: 'freezer_rooms', tooltip: 'Typically below 0°C (32°F)' },
          { title: "Cooler/Chiller Rooms", key: 'cooler_chiller_rooms', tooltip: 'Above freezing, for produce, dairy, etc' },
          { title: "Blast Freezers", key: 'blast_freezers', tooltip: 'For rapid freezing' },
        ]
      },
      {
        category: 'Support and Operational Areas', subrows: [
          { title: "Loading Docks", key: 'loading_docks', tooltip: 'With dock seals and levelers to maintain temperature' },
          { title: "Staging/Ante-Room Areas", key: 'staging_ante_room_areas', tooltip: 'A temperature buffer zone' },
          { title: "Refrigeration Plant/Engine Room", key: 'refrigeration_plant_engine_room', tooltip: 'Housing compressors and condensers.' },
          { title: "Dry Storage Area", key: 'dry_storage_area', tooltip: 'For non-refrigerated goods and packaging' },
          { title: "Office Space", key: 'office_space' },
          { title: "Battery Charging Area for Forklifts", key: 'battery_charging_area_for_forklifts' },
          { title: "Employee Welfare Areas (break rooms, lockers)", key: 'employee_welfare_areas_break_rooms_lockers' },
        ]
      }]
  }
  if (specialPurposeTypeID == 9) {
    return [
      {
        category: 'Attractions', subrows: [
          { title: "Rides", key: 'rides', tooltip: 'Roller coasters, carousels, water flumes, etc' },
          { title: "Themed Lands/Zones", key: 'themed_lands_zones' },
          { title: "Live Entertainment Venues/Theaters", key: 'live_entertainment_venues_theaters' },
          { title: "Midway/Game Stalls", key: 'midway_game_stalls' },
          { title: "Water Park Elements", key: 'water_park_elements', tooltip: 'Slides, wave pools, lazy rivers' },
        ]
      },
      {
        category: 'Guest Services and Retail', subrows: [
          { title: "Main Entrance/Ticketing Plazas", key: 'main_entrance_ticketing_plazas' },
          { title: "Restaurants, Food Carts, and Concession Stands", key: 'restaurants_food_carts_and_concession_stands' },
          { title: "Retail/Gift Shops", key: 'retail_gift_shops' },
          { title: "Restrooms", key: 'restrooms' },
          { title: "First Aid Stations", key: 'first_aid_stations' },
          { title: "Guest Relations/Information Booths", key: 'guest_relations_information_booths' },
        ]
      },
      {
        category: 'Infrastructure and Support', subrows: [
          { title: "Central Kitchens", key: 'central_kitchens' },
          { title: "Warehousing", key: 'warehousing' },
          { title: "Maintenance and Fabrication Shops", key: 'maintenance_and_fabrication_shops' },
          { title: "Administrative Buildings", key: 'administrative_buildings' },
          { title: "Parking Lots", key: 'parking_lots' },
          { title: "Landscaping and Themed Environments", key: 'landscaping_and_themed_environments' },
        ]
      }]
  }
  if (specialPurposeTypeID == 10) {
    return [
      {
        category: 'Burial Grounds', subrows: [
          { title: "Burial plots", key: 'burial_plots' },
          { title: "Mausoleums (Above-ground crypts)", key: 'mausoleums_above_ground_crypts' },
          { title: "Columbarium Niches (for cremated remains)", key: 'columbarium_niches_for_cremated_remains' },
          { title: "Cremation Gardens/Scattering Areas", key: 'cremation_gardens_scattering_areas' },
        ]
      },
      {
        category: 'Infrastructure and Buildings', subrows: [
          { title: "Internal Roadways and Paths", key: 'internal_roadways_and_paths' },
          { title: "Administration Building/Office", key: 'administration_building_office' },
          { title: "Chapel/Committal Service Shelter", key: 'chapel_committal_service_shelter' },
          { title: "Maintenance Building and Equipment Storage", key: 'maintenance_building_and_equipment_storage' },
          { title: "Landscaping, Fencing, and Gates", key: 'landscaping_fencing_and_gates' },
          { title: "Visitor Parking", key: 'visitor_parking' },
        ]
      }]
  }
  if (specialPurposeTypeID == 11) {
    return [
      {
        category: 'Public and Family Areas', subrows: [
          { title: "Chapel/Service Room", key: 'chapel_service_room' },
          { title: "Viewing/Visitation Rooms (Reposing Rooms)", key: 'viewing_visitation_rooms_reposing_rooms' },
          { title: "Family Lounge/Receiving Area", key: 'family_lounge_receiving_area' },
          { title: "Lobby/Foyer", key: 'lobby_foyer' },
          { title: "Arrangement Conference Rooms", key: 'arrangement_conference_rooms' },
        ]
      },
      {
        category: 'Private / Operational Areas', subrows: [
          { title: "Embalming/Preparation Room", key: 'embalming_preparation_room' },
          { title: "Casket Selection Room", key: 'casket_selection_room' },
          { title: "Crematory (if on-site)", key: 'crematory_if_on_site' },
          { title: "Garage (for hearses and service vehicles)", key: 'garage_for_hearses_and_service_vehicles' },
          { title: "Administrative Offices", key: 'administrative_offices' },
          { title: "Storage for supplies", key: 'storage_for_supplies' },
        ]
      }]
  }
  if (specialPurposeTypeID == 12) {
    return [
      {
        category: 'Production Facilities', subrows: [
          { title: "Crush Pad", key: 'crush_pad', tooltip: 'Where grapes are received and processed' },
          { title: "Fermentation Hall", key: 'fermentation_hall', tooltip: 'With fermentation tanks (stainless steel, concrete, or oak)' },
          { title: "Barrel Aging Cellar/Room (Cave)", key: 'barrel_aging_cellar_room_cave' },
          { title: "Bottling and Labeling Line", key: 'bottling_and_labeling_line' },
          { title: "Laboratory", key: 'laboratory' },
          { title: "Case Goods Storage/Warehouse", key: 'case_goods_storage_warehouse' },
        ]
      },
      {
        category: 'Visitor and Retail Areas', subrows: [
          { title: "Tasting Room", key: 'tasting_room' },
          { title: "Retail Shop", key: 'retail_shop' },
          { title: "Event Space/Restaurant/Patio", key: 'event_space_restaurant_patio' },
          { title: "Vineyards (land component)", key: 'vineyards_land_component' },
          { title: "Administrative Offices", key: 'administrative_offices' },
          { title: "Parking", key: 'parking' },
        ]
      }
    ]
  }
  if (specialPurposeTypeID == 13) {
    return [
      {
        category: 'Bowling area', subrows: [
          { title: "Lanes", key: 'lanes', tooltip: 'Including approaches, gutters, and pin decks.' },
          { title: "Pinsetter Machines (in the back)", key: 'pinsetter_machines_in_the_back' },
          { title: "Ball Return System", key: 'ball_return_system' },
          { title: "Bowlers' Seating Area", key: 'bowlers_seating_area' },
          { title: "Scoring System and Monitors", key: 'scoring_system_and_monitors' },
        ]
      },
      {
        category: 'Support and Entertainment Areas', subrows: [
          { title: "Customer Service/Control Desk", key: 'customer_service_control_desk' },
          { title: "Shoe Rental Counter", key: 'shoe_rental_counter' },
          { title: "Pro Shop", key: 'pro_shop' },
          { title: "Arcade/Game Room", key: 'arcade_game_room' },
          { title: "Bar/Lounge Area", key: 'bar_lounge_area' },
          { title: "Snack Bar/Restaurant with Kitchen", key: 'snack_bar_restaurant_with_kitchen' },
          { title: "Party/Meeting Rooms", key: 'party_meeting_rooms' },
          { title: "Restrooms", key: 'restrooms' },
          { title: "Back-of-house mechanical and storage rooms", key: 'back_of_house_mechanical_and_storage_rooms' },
        ]
      }
    ]
  }
  if (specialPurposeTypeID == 14) {
    return [
      {
        category: 'The Course itself', subrows: [
          { title: "Holes", key: 'holes', tooltip: 'Comprising teeing grounds, fairways, rough, and greens' },
          { title: "Hazards", key: 'hazards', tooltip: 'Bunkers (sand traps), water hazards (ponds, creeks)' },
          { title: "Practice Facilities", key: 'practice_facilities', tooltip: 'Driving range, putting green, chipping area' },
          { title: "Cart Paths", key: 'cart_paths' },
        ]
      },
      {
        category: 'Building & structures', subrows: [
          { title: "Clubhouse", key: 'clubhouse' },
          { title: "Maintenance Facility (Superintendent's Shed)", key: 'maintenance_facility_superintendent_s_shed' },
          { title: "Cart Barn/Storage", key: 'cart_barn_storage' },
          { title: "Halfway House/Snack Shack on the course", key: 'halfway_house_snack_shack_on_the_course' },
          { title: "Parking", key: 'parking' },
        ]
      },
      {
        category: 'Infrastructure', subrows: [
          { title: "Irrigation System and Pump House", key: 'irrigation_system_and_pump_house' },
          { title: "Lighting System", key: 'lighting_system' },
        ]
      }
    ]
  }
  if (specialPurposeTypeID == 15) {
    return [
      {
        category: 'Inmate Housing', subrows: [
          { title: "Cell Blocks (General Population)", key: 'cell_blocks_general_population', tooltip: 'With individual cells or dormitory-style layouts' },
          { title: "Segregation/Solitary Confinement Units", key: 'segregation_solitary_confinement_units' },
          { title: "Medical/Psychiatric Housing Units", key: 'medical_psychiatric_housing_units' },
        ]
      },
      {
        category: 'Core facilities', subrows: [
          { title: "Intake/Receiving and Discharge Area", key: 'intake_receiving_and_discharge_area' },
          { title: "Control Room/Central Command", key: 'control_room_central_command' },
          { title: "Visitation Area (contact and non-contact)", key: 'visitation_area_contact_and_non_contact' },
          { title: "Mess Hall/Cafeteria and Kitchen", key: 'mess_hall_cafeteria_and_kitchen' },
          { title: "Medical and Dental Clinic", key: 'medical_and_dental_clinic' },
          { title: "Educational/Vocational Training Classrooms and Workshops", key: 'educational_vocational_training_classrooms_and_workshops' },
          { title: "Library", key: 'library' },
          { title: "Chapel", key: 'chapel' },
          { title: "Recreation Yard/Gymnasium", key: 'recreation_yard_gymnasium' },
          { title: "Laundry Facilities", key: 'laundry_facilities' },
        ]
      },
      {
        category: 'Secure and Administration', subrows: [
          { title: "Perimeter Fencing, Walls, and Towers", key: 'perimeter_fencing_walls_and_towers' },
          { title: "Sally Ports/Secure Entrances", key: 'sally_ports_secure_entrances' },
          { title: "Administrative Building", key: 'administrative_building' },
          { title: "Staff Facilities (lockers, briefing rooms)", key: 'staff_facilities_lockers_briefing_rooms' },
          { title: "Armoury", key: 'armoury' },
          { title: "Visitor Screening Area", key: 'visitor_screening_area' },
        ]
      }
    ]
  }
  if (specialPurposeTypeID == 16) {
    return [
      {
        category: 'Core IT Space', subrows: [
          { title: "Data Halls", key: 'data_halls', tooltip: 'This is the primary area housing server racks, network equipment, and storage arrays' },
          { title: "Server Racks/Cabinets", key: 'server_racks_cabinets', tooltip: 'Standardized enclosures for mounting IT hardware' },
          { title: "Raised Flooring or Structural Ceiling Grids", key: 'raised_flooring_or_structural_ceiling_grids', tooltip: 'To manage cabling and airflow distribution.' },
          { title: "Hot/Cold Aisle Containment Systems", key: 'hot_cold_aisle_containment_systems', tooltip: 'Physical barriers to separate server air intake (cold) from exhaust (hot), improving cooling efficiency.' },
          { title: "Cable Trays and Raceways", key: 'cable_trays_and_raceways', tooltip: 'Overhead or underfloor systems for organizing power and data cabling.' },
        ]
      },
      {
        category: 'Poer Infrastructure', subrows: [
          { title: "Utility Power Feeds", key: 'utility_power_feeds', tooltip: 'Multiple, independent connections to the local power grid' },
          { title: "Main Switchgear", key: 'main_switchgear', tooltip: 'Distributes utility power throughout the facility.' },
          { title: "Uninterruptible Power Supply (UPS) Systems", key: 'uninterruptible_power_supply_ups_systems', tooltip: 'Large battery rooms that provide instant power in case of a utility outage, allowing for a seamless transition to generator power.' },
          { title: "Backup Generators", key: 'backup_generators', tooltip: 'Diesel or natural gas generators that can power the entire facility for an extended period. Often includes on-site fuel storage tanks' },
          { title: "Power Distribution Units (PDUs)", key: 'power_distribution_units_pdus', tooltip: 'Step down high-voltage power and distribute it to the server racks' },
          { title: "Automatic Transfer Switches (ATS)", key: 'automatic_transfer_switches_ats', tooltip: 'Automatically switch the power source from utility to generator during an outage' },
        ]
      },
      {
        category: 'Cooling Infrastructure', subrows: [
          { title: "Computer Room Air Handler (CRAH) or Air Conditioner (CRAC) Units:", key: 'computer_room_air_handler_crah_or_air_conditioner_crac_units', tooltip: 'These units circulate air through the data hall, removing heat' },
          { title: "Chillers", key: 'chillers', tooltip: 'A central plant that cools water or a refrigerant, which is then pumped to the CRAH/CRAC units' },
          { title: "Cooling Towers", key: 'cooling_towers', tooltip: 'Expel heat from the chiller water into the atmosphere (for water-cooled systems)' },
          { title: "Piping and Pumping Systems", key: 'piping_and_pumping_systems', tooltip: 'A network of pipes to circulate the chilled fluid' },
          { title: "Economizers", key: 'economizers', tooltip: 'Systems that use cool outside air to supplement or replace mechanical cooling, saving energy' },
        ]
      },
      {
        category: 'Support & Ancillary areas', subrows: [
          { title: "Network Operations Center (NOC)", key: 'network_operations_center_noc', tooltip: 'A control room where technicians monitor the performance of the data center and its networks' },
          { title: "Meet-Me Rooms (MMRs)", key: 'meet_me_rooms_mmrs', tooltip: "Secure rooms where different telecommunication companies and internet service providers connect to the data center's network infrastructure" },
          { title: "Loading Docks", key: 'loading_docks', tooltip: 'For secure delivery and removal of equipment.' },
          { title: "Staging Rooms", key: 'staging_rooms', tooltip: 'Areas to unbox, configure, and test equipment before it is moved into the data hall.' },
          { title: "Storage Rooms", key: 'storage_rooms', tooltip: 'For spare parts and other equipment.' },
          { title: "Administrative Offices & Employee Welfare", key: 'administrative_offices_employee_welfare', tooltip: 'Office space for staff, break rooms, and restrooms.' },
        ]
      }
    ]
  }
  return []
}

type DefaultAccomodationRow = {
  kind: 'default',
  rows: { title: string, key: string }[]
}
type SpecialAccomodationRow = {
  kind: 'special',
  rows: {
    category: string,
    key?: string,
    tooltip?: string
    subrows: { title: string, key: string, tooltip?: string }[]
  }[]
}

type AccomodationRow = DefaultAccomodationRow | SpecialAccomodationRow